import type { Event } from "../engine/events/event";
import { EventParameter } from "../engine/events/eventParameter";
import { EventType } from "../engine/events/eventType";
import { Strings } from "../engine/strings";

export interface StyledText {
	text: string;
	color: string;
	bold: boolean;
}

function white(text: string): StyledText {
	return { text, color: "#ffffff", bold: false };
}

function colored(text: string, color: string, bold = false): StyledText {
	return { text, color, bold };
}

function getEventString(event: Event, ...params: unknown[]): string | null {
	const key = "event_" + EventType[event.type];
	return Strings.get(key, ...params);
}

export function formatEvent(event: Event): StyledText[] {
	switch (event.type) {
		case EventType.PLAYER_DIED:
			return [colored((getEventString(event) ?? "You died.") + "\n", "#ff0000", true)];

		case EventType.GOT_AMMO: {
			const amount = event.getParameter(EventParameter.AMMO_AMOUNT) ?? "?";
			const ammoNameKey = event.getParameter(EventParameter.AMMO_NAME) ?? "ammo";
			const ammoName = Strings.get(ammoNameKey) ?? ammoNameKey;
			return [white((getEventString(event, amount) ?? `Got ${amount}x`) + " "), colored(ammoName + ".\n", "#00bfff")];
		}

		case EventType.GOT_ITEM: {
			const amount = event.getParameter(EventParameter.AMMO_AMOUNT) ?? "?";
			const itemNameKey = event.getParameter(EventParameter.AMMO_NAME) ?? "item";
			const itemName = Strings.get(itemNameKey) ?? itemNameKey;
			return [white((getEventString(event, amount) ?? `Got ${amount}x`) + " "), colored(itemName + ".\n", "#ffcc00")];
		}

		case EventType.GOT_WEAPON: {
			const weaponNameKey = event.getParameter(EventParameter.WEAPON_NAME) ?? "weapon";
			const weaponName = Strings.get(weaponNameKey) ?? weaponNameKey;
			return [white((getEventString(event) ?? "You got the") + " "), colored(weaponName + "!\n", "#ff4500", true)];
		}

		case EventType.REACHED_AREA: {
			const areaName = event.getParameter(EventParameter.AREA_NAME) ?? "???";
			return [colored((getEventString(event, areaName) ?? `You have reached: ${areaName}`) + ".\n", "#00ff00")];
		}

		case EventType.PLAYER_HEALED:
			return [colored((getEventString(event) ?? "You healed your wounds.") + "\n", "#00ff00")];

		case EventType.DO_NOT_HAVE_KEY:
			return [white((getEventString(event) ?? "You need a key to open this crate.") + "\n")];

		case EventType.CONTAINER_OPENS:
			return [white((getEventString(event) ?? "You opened the crate.") + "\n")];

		case EventType.LEVEL_DIRECTOR_HOSTILITY_REACHED_1:
			return [colored((getEventString(event) ?? "More enemies are coming.") + "\n", "#ff4500", true)];

		case EventType.LEVEL_DIRECTOR_HOSTILITY_REACHED_2:
			return [colored((getEventString(event) ?? "This area is getting dangerous.") + "\n", "#ff4500", true)];

		case EventType.LEVEL_DIRECTOR_HOSTILITY_REACHED_3:
			return [colored((getEventString(event) ?? "Even more enemies approach.") + "\n", "#ff4500", true)];

		case EventType.LEVEL_DIRECTOR_HOSTILITY_REACHED_4:
			return [
				colored(
					(getEventString(event) ?? "They are very angry and are sending reinforcements.") + "\n",
					"#ff4500",
					true,
				),
			];

		case EventType.LEVEL_DIRECTOR_HOSTILITY_REACHED_5:
			return [colored((getEventString(event) ?? "BLOOD FOR THE BLOOD GOD!") + "\n", "#ff4500", true)];

		case EventType.TERMINAL_OPENED_BLAST_DOORS:
			return [white((getEventString(event) ?? "You access the terminal. The blast doors open!") + "\n")];

		case EventType.EQUATION_IS_HERE:
			return [
				colored(
					(getEventString(event) ?? "The equation is here! You just need to take a closer look...") + "\n",
					"#ffff00",
				),
			];

		case EventType.EQUATION_FOUND:
			return [colored((getEventString(event) ?? "You have found the equation!") + "\n", "#ffff00")];

		case EventType.PLAYER_FULLY_HEALED:
			return [colored((getEventString(event) ?? "You are now fully healed!") + "\n", "#00ff00")];

		case EventType.CANT_HEAL_ALREADY_FULL_HEALTH:
			return [white((getEventString(event) ?? "You are already at full health.") + "\n")];

		case EventType.CANT_PUT_MINE_HERE:
			return [white((getEventString(event) ?? "Can't deploy a mine here.") + "\n")];

		case EventType.NO_PATH_FOR_AUTOMOVE:
			return [white((getEventString(event) ?? "You don't know how to get there.") + "\n")];

		case EventType.DEV_KILL_BOT_ACTIVATED:
			return [white((getEventString(event) ?? "Auto kill activated.") + "\n")];

		case EventType.DEV_KILL_BOT_DEACTIVATED:
			return [white((getEventString(event) ?? "Auto kill deactivated.") + "\n")];

		case EventType.FIRST_WEAPON_PICKUP:
			return [];

		case EventType.LOW_HP_MEDIKIT_HINT:
			return [];

		case EventType.HINT:
			return [];

		default:
			return [white((getEventString(event) ?? "Event: " + EventType[event.type] + ".") + "\n")];
	}
}
